import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import apiClient from "../apiClient";
import { formatMoney } from "../formatMoney";
import PageContext from "../PageContext.jsx";

const AssetsAdd = () => {
  const [currencies, setCurrencies] = useState([]);
  const [asset, setAsset] = useState({
    Description: "",
    PurchaseValue: "",
    CurrentValue: "",
    CurrentValuationDate: "",
    CurrencyID: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const sortedCurrencies = useMemo(
    () => [...currencies].sort((a, b) => (a.Description || "").localeCompare(b.Description || "")),
    [currencies]
  );
  const selectedCurrency = useMemo(
    () => currencies.find((currency) => String(currency.CurrencyID) === String(asset.CurrencyID)) || null,
    [currencies, asset.CurrencyID]
  );

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const res = await apiClient.get("/currencies");
        const rows = Array.isArray(res.data) ? res.data : [];
        const sorted = [...rows].sort((a, b) => (a.Description || "").localeCompare(b.Description || ""));
        setCurrencies(sorted);
        if (sorted.length > 0) {
          setAsset((prev) => ({
            ...prev,
            CurrencyID: rows.find((row) => Number(row.IsDefault))?.CurrencyID || sorted[0].CurrencyID,
          }));
        }
      } catch (err) {
        setError("Could not load currencies.");
      }
    };
    fetchCurrencies();
  }, []);

  const handleChange = (e) => {
    setAsset((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setSuccess("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!asset.Description?.trim() || !asset.CurrencyID) {
      setError("Description and currency are required.");
      return;
    }

    try {
      await apiClient.post("/assets", {
        Description: asset.Description,
        PurchaseValue: Number(asset.PurchaseValue || 0),
        CurrentValue: Number(asset.CurrentValue || 0),
        CurrentValuationDate: asset.CurrentValuationDate || null,
        CurrencyID: Number(asset.CurrencyID),
      });
      setError("");
      setSuccess("Asset saved.");
      setAsset((prev) => ({ ...prev, Description: "", PurchaseValue: "", CurrentValue: "", CurrentValuationDate: "" }));
    } catch (err) {
      setError("Could not save asset. Please try again.");
      setSuccess("");
    }
  };

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Enter Asset</h1>
          <p className="page-subtitle">
            <Link to="/">Home</Link> / Assets / Add
          </p>
        </div>
      </header>
      <PageContext
        items={[
          { label: "Home", to: "/" },
          { label: "Assets", to: "/Assets" },
          { label: "Enter Asset" },
        ]}
        note="Record an asset, its purchase value, and the latest valuation."
      />

      <section className="panel form-card">
        <div className="form">
          <label htmlFor="asset-description">Description</label>
          <input
            id="asset-description"
            type="text"
            name="Description"
            placeholder="e.g. Laptop"
            value={asset.Description}
            onChange={handleChange}
          />

          <label htmlFor="asset-purchase-value">Purchase Value</label>
          <input
            id="asset-purchase-value"
            type="number"
            min="0"
            step="0.01"
            name="PurchaseValue"
            placeholder="0.00"
            value={asset.PurchaseValue}
            onChange={handleChange}
          />
          <div className="form-value-preview">
            Display: {formatMoney(asset.PurchaseValue || 0, selectedCurrency?.Description)}
          </div>

          <label htmlFor="asset-currency">Currency</label>
          <select
            id="asset-currency"
            name="CurrencyID"
            value={asset.CurrencyID}
            onChange={handleChange}
          >
            {sortedCurrencies.map((currency) => (
              <option key={currency.CurrencyID} value={currency.CurrencyID}>
                {currency.Description}
              </option>
            ))}
          </select>

          <label htmlFor="asset-current-value">Current Value</label>
          <input
            id="asset-current-value"
            type="number"
            min="0"
            step="0.01"
            name="CurrentValue"
            placeholder="0.00"
            value={asset.CurrentValue}
            onChange={handleChange}
          />
          <div className="form-value-preview">
            Display: {formatMoney(asset.CurrentValue || 0, selectedCurrency?.Description)}
          </div>

          <label htmlFor="asset-current-valuation-date">Date Of Current Valuation</label>
          <input
            id="asset-current-valuation-date"
            type="date"
            name="CurrentValuationDate"
            value={asset.CurrentValuationDate}
            onChange={handleChange}
          />

          <div className="form-actions">
            <button className="btn" onClick={handleSubmit}>
              Save Asset
            </button>
            <Link className="btn-link" to="/Assets">
              View Assets
            </Link>
            <Link className="btn-link" to="/">
              Back Home
            </Link>
          </div>

          {error && <p className="notice notice-error">{error}</p>}
          {success && <p className="notice notice-muted">{success}</p>}
        </div>
      </section>
    </div>
  );
};

export default AssetsAdd;
